/* @flow */

import { apiRoot } from 'src/shared/app/base/api/apiRoot';
import { TAG_TYPE } from 'src/shared/app/base/api/apiConstant';
import { infiniteLoadingSettings } from 'src/shared/app/base/api/utils/endpointUtils';
import {
  getNotificationId,
  getNotificationMarkReadURL,
  getNotificationClearURL,
} from '@braindate/domain/lib/notification/util';
import {
  clearAllNotificationsEndpoint,
  notificationsEndpoint,
} from 'src/shared/app/base/settings/endpointSettings';

import type { Notification } from '@braindate/domain/lib/notification/type';
import type {
  UseQuery,
  UseMutation,
  ApiListResult,
} from 'src/shared/app/base/api/type/apiQueryType';
import { defaultLimitPages } from 'src/shared/app/base/api/constant/apiConstant';

type Api = {
  useGetNotificationsQuery: UseQuery<ApiListResult<Notification>>,
  useClearAllNotificationsMutation: UseMutation<void>,
  useMarkNotificationAsReadMutation: UseMutation<Notification>,
  useDismissNotificationMutation: UseMutation<Notification>,
};

const extendedApi: Api = apiRoot.injectEndpoints({
  endpoints: (builder) => ({
    getNotifications: builder.query({
      query: ({ page = 1, limit } = {}) => ({
        url: notificationsEndpoint(),
        refetchOnFocus: true,
        refetchOnReconnect: true,
        params: {
          limit: limit || defaultLimitPages,
          offset: (page - 1) * (limit || defaultLimitPages),
        },
      }),
      ...infiniteLoadingSettings<Notification>({
        tagType: TAG_TYPE.NOTIFICATION,
        idGetter: getNotificationId,
      }),
    }),
    clearAllNotifications: builder.mutation({
      query: () => ({
        url: clearAllNotificationsEndpoint(),
        method: 'post',
      }),
    }),
    markNotificationAsRead: builder.mutation({
      query: (notification: Notification) => ({
        url: getNotificationMarkReadURL(notification),
        method: 'post',
      }),
      invalidatesTags: [TAG_TYPE.NOTIFICATION],
    }),
    dismissNotification: builder.mutation({
      query: (notification: Notification) => ({
        url: getNotificationClearURL(notification),
        method: 'post',
      }),
      invalidatesTags: [TAG_TYPE.NOTIFICATION],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetNotificationsQuery,
  useClearAllNotificationsMutation,
  useMarkNotificationAsReadMutation,
  useDismissNotificationMutation,
} = extendedApi;
